// Guided: add a Data Target (where the pipeline writes — Postgres/MySQL/Mongo).
// Domain-agnostic. Drives the assistant to register a target the user describes,
// then bind elements to it. Gates on a target storage appearing.
import { listStorages } from "../../../services/pipeline";

async function hasTarget() {
  try {
    const r = await listStorages();
    const arr = Array.isArray(r) ? r : (r?.storages || []);
    return arr.some((s) => /^(postgres|mysql|mongo)/.test(s.storage_id || "") ||
      ["postgres", "mysql", "mongo"].includes((s.storage_type || s.type || "").toLowerCase()));
  } catch { return false; }
}

export const dataTargetGuide = {
  key: "data_target",
  title: "Cortex Guided · Add a data target",
  makeSteps(engine, ctx) {
    const { setStatus, chatStep, chatSend, waitChatIdle, goPage, refreshPages } = engine;
    return [
      {
        title: "Add a data target",
        lines: [
          "A <b>data target</b> is where the pipeline lands transformed records —",
          "a relational DB (Postgres/MySQL) or Mongo. The pipeline's final",
          "transformation writes here. Press Next.",
        ],
        run: async () => { goPage("/pipeline"); setStatus("Ready. Press Next, then describe the target."); },
      },
      {
        title: "1 · Declare the destination",
        lines: [
          "Tell the assistant where to write: <i>“a Postgres database analytics on",
          "host warehouse.internal”</i>. It registers it with add_postgres_target",
          "(or add_mongo_target). Connection details stay in the recipe config.",
        ],
        run: async () => {
          goPage("/pipeline");
          const ok = await chatStep(
            "Help me add a data TARGET to this project. Ask me nothing up front — from my description register a Postgres target with add_postgres_target (a sensible name + the host/db I give; if I gave none, register a target named `analytics_pg` as a placeholder I can edit). Report the registered target when done, and tell me I can bind data elements to it next.",
            async () => await hasTarget(),
            { totalMs: 240_000, statusFn: async (s) => { refreshPages(); return `registering target… (${s}s)`; } },
          ).catch(() => false);
          refreshPages();
          setStatus(ok ? "✓ target registered" : "⚠ not yet — describe your target in the chat, or Next to retry");
        },
        autoAdvanceReady: () => false,
      },
      {
        title: "2 · Bind elements (optional)",
        lines: [
          "A target receives data via element → column bindings.",
          "Ask the assistant to bind your data elements to this target's table,",
          "or skip — you can wire the DAG to it later on the Pipeline page.",
        ],
        run: async () => {
          goPage("/pipeline");
          await chatSend(
            "If I name a table + elements, bind each data element to this target's table with bind_element_to_target; otherwise just confirm the target is registered. Don't ask for confirmation — act directly.",
          );
          await waitChatIdle(180_000);
          refreshPages();
          setStatus("✓ done — target is configured for this project");
        },
      },
    ];
  },
};
